import type { FormEvent } from 'react';

interface RequestAccessFormProps {
  reqName: string;
  reqEmail: string;
  reqReason: string;
  reqSuccess: boolean;
  loading: boolean;
  error: string | null;
  onNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onReasonChange: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  onBackToLogin: () => void;
  onSuccessBack: () => void;
}

export function RequestAccessForm({
  reqName, reqEmail, reqReason, reqSuccess, loading, error,
  onNameChange, onEmailChange, onReasonChange, onSubmit, onBackToLogin, onSuccessBack,
}: RequestAccessFormProps) {
  return (
    <div className="max-w-sm w-full mx-auto">
      <button
        type="button"
        onClick={onBackToLogin}
        className="flex items-center gap-1.5 text-xs text-saas-muted hover:text-saas-text mb-5 transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to login
      </button>

      {reqSuccess ? (
        <div className="text-center space-y-4">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-saas-text">Request Submitted!</p>
            <p className="text-xs text-saas-muted mt-1.5 leading-relaxed">
              An administrator will review your request and create your account. You&apos;ll be notified at{' '}
              <span className="font-medium text-saas-text">{reqEmail}</span>.
            </p>
          </div>
          <button type="button" onClick={onSuccessBack} className="btn-secondary w-full">
            Back to Login
          </button>
        </div>
      ) : (
        <>
          <h2 className="text-lg font-bold text-saas-text mb-1">Request Access</h2>
          <p className="text-sm text-saas-muted mb-5">
            Fill in your details and an admin will create your account.
          </p>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-saas-text mb-1.5">Full Name *</label>
              <input type="text" required value={reqName} onChange={e => onNameChange(e.target.value)}
                placeholder="Your full name" className="input" disabled={loading} />
            </div>
            <div>
              <label className="block text-sm font-medium text-saas-text mb-1.5">Email Address *</label>
              <input type="email" required value={reqEmail} onChange={e => onEmailChange(e.target.value)}
                placeholder="you@company.com" className="input" disabled={loading} />
            </div>
            <div>
              <label className="block text-sm font-medium text-saas-text mb-1.5">Reason <span className="text-saas-muted font-normal">(optional)</span></label>
              <textarea value={reqReason} onChange={e => onReasonChange(e.target.value)} rows={2}
                placeholder="Why do you need access?" disabled={loading}
                className="w-full px-3 py-2.5 border border-saas-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none text-saas-text placeholder:text-gray-400" />
            </div>
            <button type="submit" disabled={loading || !reqName || !reqEmail} className="btn-primary w-full">
              {loading
                ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Submitting...</>
                : 'Submit Request'}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
